import Phaser from 'phaser';
import { currentDpr, fitCanvas } from './game/ui/viewport';
import { gameStore } from './game/simulation/GameStore';
import { BootScene } from './game/scenes/BootScene';
import { MainMenuScene } from './game/scenes/MainMenuScene';
import { CityScene } from './game/scenes/CityScene';
import { ParkingLotScene } from './game/scenes/ParkingLotScene';
import { UIScene } from './game/scenes/UIScene';

/**
 * Application entry point. Wires up Phaser and mounts the game into #game-root.
 * All game logic lives in the simulation engine (GameStore/GameSimulation);
 * Phaser is only the renderer/input layer (spec sections 1 & 42).
 *
 * High-DPI crispness is handled in ui/viewport.ts: the canvas backing store is
 * (CSS size × DPR) while displayed at CSS size. BootScene settles the DPR
 * before the menu renders; here we only react to *later* DPR changes.
 */
const bootDpr = currentDpr();

const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  parent: 'game-root',
  backgroundColor: '#0b1220',
  scale: {
    // Manual sizing so we can drive the backing-store resolution by DPR.
    mode: Phaser.Scale.NONE,
    width: window.innerWidth * bootDpr,
    height: window.innerHeight * bootDpr,
  },
  render: {
    pixelArt: false,
    antialias: true,
    roundPixels: true,
  },
  scene: [BootScene, MainMenuScene, CityScene, ParkingLotScene, UIScene],
};

const game = new Phaser.Game(config);

/** The DPR the currently-rendered scenes were laid out for. */
let appliedDpr = bootDpr;

function restartActiveScenes(): void {
  for (const scene of game.scene.getScenes(true)) {
    // Never restart the boot scene; it drives the startup transition.
    if (scene.scene.key === 'BootScene') continue;
    scene.scene.restart();
  }
}

/**
 * Re-fit to the window. If the DPR actually changed after boot (e.g. dragging
 * the window to another monitor), scenes were laid out at the old scale, so
 * restart them to redraw at the new scale.
 */
function refit(): void {
  const dpr = fitCanvas(game);
  if (dpr !== appliedDpr) {
    appliedDpr = dpr;
    restartActiveScenes();
  }
}

window.addEventListener('resize', refit);

// Track DPR changes (e.g. moving between monitors) without polling.
function watchDprChange(): void {
  if (typeof matchMedia !== 'function') return;
  const mq = matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
  mq.addEventListener(
    'change',
    () => {
      refit();
      watchDprChange(); // re-arm for the next threshold
    },
    { once: true }
  );
}
watchDprChange();

// Dev-only: expose for inspection / the future debug panel (spec section 40).
if (import.meta.env.DEV) {
  Object.assign(window as unknown as Record<string, unknown>, {
    __game: game,
    __store: gameStore,
  });
}
